/**
 * Hero Studio — the artist chooses the image their portfolio leads with.
 *
 * This is where onboarding hands off. An artist arrives having picked one of three styles, and
 * the first thing they do in the admin is see their own work looking good. It is also the one
 * task in the whole product that genuinely needs their eye rather than an operator's.
 *
 * Two routes to a hero, deliberately distinct:
 *  - GENERATE bespoke concepts for one piece (~25s and real money per render, so candidates are
 *    explicit and never fire on mount).
 *  - APPLY a library template across every piece at once — one scene paid for once, composited
 *    into the whole catalog for effectively nothing.
 *
 * Deep link: /easel/hero?artwork=44583 preselects a piece, which is how onboarding lands someone
 * straight on their flagship.
 */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Check, Loader2, Sparkles, Layers, AlertTriangle, Compass } from 'lucide-react'
import {
  useMockupContext,
  useMockupTemplates,
  useGenerateCandidates,
  useSetHero,
  useApplyTemplate,
  useSceneTemplates,
  useProposeDirections,
  useBuildDirection,
  useApplyScenes,
  useMockupTargets,
} from '../hooks/useMockups'
import type { MockupSlide, SceneDirection } from '../api/easelApi'

/**
 * The render target selects which mockup set Easel serves.
 *
 * An explicit ?target= still wins, so existing links keep working. Without one
 * the page now asks the API which targets this artist's work actually belongs
 * to and uses the most-rendered. It used to simply render "0 of N with a hero"
 * and tell the artist to hand-edit the URL, which nobody was going to find.
 */
function useTarget(defaultTarget?: string | null) {
  const [params] = useSearchParams()
  return params.get('target') || defaultTarget || ''
}

export function HeroStudioPage() {
  const [params] = useSearchParams()
  const { data: targets, isLoading: isLoadingTargets } = useMockupTargets()
  const target = useTarget(targets?.defaultTarget)
  const { data: context, isLoading } = useMockupContext(target || undefined)
  const { data: templates } = useMockupTemplates()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [chosen, setChosen] = useState<string[]>(['flatlay'])
  const [motif, setMotif] = useState('')
  const [candidates, setCandidates] = useState<MockupSlide[]>([])

  const generate = useGenerateCandidates()
  const propose = useProposeDirections()
  const buildDirection = useBuildDirection(target || undefined)
  const applyScenes = useApplyScenes(target || undefined)
  const { data: sceneTemplates } = useSceneTemplates(target || undefined)
  const [directions, setDirections] = useState<SceneDirection[]>([])
  const setHero = useSetHero(target || undefined)
  const applyTemplate = useApplyTemplate(target || undefined)

  // Preselect from the deep link, else the first piece — so the page is never empty on arrival.
  useEffect(() => {
    if (selectedId || !context?.artworks.length) return
    const wanted = Number(params.get('artwork'))
    const hit = context.artworks.find((a) => a.id === wanted)
    setSelectedId(hit ? hit.id : context.artworks[0].id)
  }, [context, params, selectedId])

  const artwork = useMemo(
    () => context?.artworks.find((a) => a.id === selectedId) ?? null,
    [context, selectedId]
  )
  const withHero = context?.artworks.filter((a) => a.hero).length ?? 0
  const total = context?.artworks.length ?? 0

  const toggleConcept = (id: string) =>
    setChosen((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))

  const onGenerate = async () => {
    if (!artwork || !chosen.length) return
    setCandidates([])
    const res = await generate.mutateAsync({
      artworkId: artwork.id,
      concepts: chosen,
      motif: motif.trim() || undefined,
      target: target || undefined,
    })
    setCandidates(res.slides || [])
  }

  const onPropose = async () => {
    if (!target) return
    setDirections([])
    const res = await propose.mutateAsync({
      target,
      count: 3,
      sampleArtworkId: artwork?.id,
    })
    setDirections(res.directions || [])
  }

  const onChoose = (slide: MockupSlide) => {
    if (!artwork || !target) return
    setHero.mutate({
      artworkId: artwork.id,
      url: slide.url,
      target,
      concept: slide.concept ?? null,
      alt: `${artwork.name} — hero`,
      width: slide.width,
      height: slide.height,
      aspect: slide.aspect,
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Hero Studio</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Choose the image each piece leads with. Every surface of your portfolio — the home
            feature, the artwork page, collection headers — prefers whatever is set here.
          </p>
        </div>
        <Badge variant={withHero === total && total > 0 ? 'default' : 'secondary'}>
          {withHero} of {total} with a hero
        </Badge>
      </header>

      {!target && !isLoadingTargets && (
        <Card className="border-amber-500/40">
          <CardContent className="flex items-start gap-3 p-4 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-muted-foreground">
              None of your artwork has been rendered for a storefront yet, so there is no hero to
              set — Easel will keep serving the default mockups. This fills in on its own once your
              pieces have been rendered.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Pick a piece ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Your work</CardTitle>
          <CardDescription>Pick a piece to give it a hero image.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8">
            {context?.artworks.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => { setSelectedId(a.id); setCandidates([]) }}
                aria-pressed={a.id === selectedId}
                className={`group relative overflow-hidden rounded-md border-2 transition-colors ${
                  a.id === selectedId ? 'border-primary' : 'border-transparent hover:border-muted-foreground/30'
                }`}
                title={a.name}
              >
                <img src={a.hero?.url || a.artUrl} alt={a.name} loading="lazy" className="aspect-[3/4] w-full object-cover" />
                {a.hero && (
                  <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Generate bespoke concepts ────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" /> Generate concepts
            {artwork && <span className="font-normal text-muted-foreground">— {artwork.name}</span>}
          </CardTitle>
          <CardDescription>
            Editorial scenes rendered around this exact piece. Each takes about half a minute and
            costs a few cents, so nothing is saved until you choose one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {context?.concepts.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleConcept(c.id)}
                aria-pressed={chosen.includes(c.id)}
                title={c.templatable ? 'Can also become a reusable template' : c.notTemplatableReason || undefined}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  chosen.includes(c.id)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input hover:bg-accent'
                }`}
              >
                {c.label}
                {c.templatable && <span className="ml-1 opacity-70">·reusable</span>}
              </button>
            ))}
          </div>

          <div className="max-w-xl space-y-1.5">
            <Label htmlFor="motif" className="text-xs">
              Motifs to style around <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="motif"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="alpine snowfields, a lone climber, warm parchment"
            />
            <p className="text-xs text-muted-foreground">
              Pulled from this piece, the props feel made for it rather than generic.
            </p>
          </div>

          <Button onClick={onGenerate} disabled={!artwork || !chosen.length || generate.isPending}>
            {generate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {generate.isPending
              ? `Rendering ${chosen.length}…`
              : `Generate ${chosen.length} concept${chosen.length === 1 ? '' : 's'}`}
          </Button>

          {candidates.length > 0 && (
            <div className="grid grid-cols-2 gap-4 pt-2 sm:grid-cols-3 lg:grid-cols-4">
              {candidates.map((s) => (
                <figure key={s.url} className="overflow-hidden rounded-md border">
                  <img src={s.url} alt={s.concept || 'concept'} loading="lazy" className="w-full" />
                  <figcaption className="flex items-center justify-between gap-2 p-2">
                    <span className="truncate text-xs text-muted-foreground">{s.concept}</span>
                    <Button size="sm" variant="secondary" disabled={!target || setHero.isPending} onClick={() => onChoose(s)}>
                      Set as hero
                    </Button>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Scene direction ──────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Compass className="h-4 w-4" /> Where your work lives
          </CardTitle>
          <CardDescription>
            Your lifestyle shots need a world, and the obvious one — a nice living room — is rarely
            the truest one. These directions are read from your own creative genome. Each preview is
            a real scene with your work in it, not an illustration of one, so what you judge is what
            ships.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" onClick={onPropose} disabled={!target || propose.isPending}>
              {propose.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {propose.isPending ? 'Imagining directions…' : 'Suggest 3 directions'}
            </Button>
            {propose.isPending && (
              <span className="text-xs text-muted-foreground">
                A minute or two — each direction is rendered so you can see it.
              </span>
            )}
            {!!sceneTemplates?.length && (
              <Badge variant="secondary">
                {sceneTemplates.length} scene{sceneTemplates.length === 1 ? '' : 's'} ready
                {sceneTemplates[0]?.directionName ? ` · ${sceneTemplates[0].directionName}` : ''}
              </Badge>
            )}
          </div>

          {directions.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {directions.map((d) => (
                <article key={d.id} className="flex flex-col overflow-hidden rounded-md border">
                  {d.preview ? (
                    <img src={d.preview} alt={d.name} loading="lazy" className="w-full" />
                  ) : (
                    <div className="flex aspect-[4/5] items-center justify-center bg-muted p-4 text-center text-xs text-muted-foreground">
                      {d.previewError || 'No preview'}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col gap-2 p-3">
                    <h3 className="text-sm font-semibold">{d.name}</h3>
                    <p className="text-xs leading-relaxed text-muted-foreground">{d.rationale}</p>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                      {d.settings.map((s) => (
                        <li key={s.id}>· {s.name}</li>
                      ))}
                    </ul>
                    <Button
                      size="sm"
                      className="mt-auto"
                      disabled={!target || !d.preview || buildDirection.isPending}
                      onClick={() => buildDirection.mutate({ target, direction: d })}
                    >
                      {buildDirection.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      Use this direction
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {!!sceneTemplates?.length && (
            <div className="rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {sceneTemplates.map((t) => (
                    <img
                      key={t.id}
                      src={t.imageUrl}
                      alt={t.sceneName}
                      title={t.sceneName}
                      loading="lazy"
                      className="h-16 w-16 rounded object-cover"
                    />
                  ))}
                </div>
                <Button
                  variant="secondary"
                  disabled={!target || applyScenes.isPending}
                  onClick={() => applyScenes.mutate({ target })}
                >
                  {applyScenes.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Put these behind all {total}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Replaces the lifestyle shots on every piece. Your hero and framed shot stay exactly
                as they are.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Apply a template to everything ───────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4" /> Apply a scene to every piece
          </CardTitle>
          <CardDescription>
            These scenes were rendered once and left empty, so your work drops straight in. Applying
            one across the catalog costs nothing and takes a few seconds per piece.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!templates?.length ? (
            <p className="text-sm text-muted-foreground">No reusable scenes in the library yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {templates.map((t) => (
                <figure key={t.id} className="overflow-hidden rounded-md border">
                  <img src={t.imageUrl} alt={t.conceptId} loading="lazy" className="w-full" />
                  <figcaption className="space-y-2 p-2">
                    <div className="truncate text-xs text-muted-foreground">
                      {t.conceptId}
                      {t.frameless ? ' · bare print' : ' · framed'}
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-full"
                      disabled={!target || applyTemplate.isPending}
                      onClick={() => applyTemplate.mutate({ templateId: t.id, target })}
                    >
                      {applyTemplate.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      Apply to all {total}
                    </Button>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default HeroStudioPage
